// main.ts — app entry. Boots Phaser into #game, wires URL params onto the registry, and installs
// the window.__game dev/test bridge. All art is procedural (M0); no asset preloads.
import Phaser from 'phaser';
import { PALETTE, hex } from './render/keys';
import { chooseDesignSpace, setDesignSpace } from './render/stage';
import { REG, parseBootParams } from './render/registryKeys';
import { BootScene } from './render/scenes/BootScene';
import { SplashScene } from './render/scenes/SplashScene';
import { MenuScene } from './render/scenes/MenuScene';
import { GameScene } from './render/scenes/GameScene';
import { ResultScene } from './render/scenes/ResultScene';
import { installHooks } from './hooks';
import { installAnalyticsDebugBridge, initExperiments, trackFirstOpen } from './analytics/client';
import { loadGrowthConfig } from './integration/growth/config';
import { initGrowth } from './integration/growth';
import { registerServiceWorker } from './pwa/register';
import { loadFonts } from './render/fonts';
import { handleFatal, reportFatal } from './render/errorBoundary';

const parent = document.getElementById('game');
// Replace the index.html loading text — Phaser appends its <canvas> to #game.
if (parent) parent.textContent = '';

// Safe-area debug override: a notched capture / test (or a manual probe) can force the
// env(safe-area-inset-*) values via localStorage so the inset is deterministic headless (real devices
// use the env() fallback in index.html). Must run BEFORE Phaser measures #game so the inset applies.
try {
  const rootStyle = document.documentElement.style;
  for (const side of ['top', 'bottom', 'left', 'right'] as const) {
    const v = localStorage.getItem(`trivia.debug.safeInset.${side}`);
    if (v != null && v !== '' && Number.isFinite(Number(v))) rootStyle.setProperty(`--tm-safe-${side}`, `${Number(v)}px`);
  }
} catch {
  /* ignore — safe-area falls back to env() in CSS */
}

// Global crash net (E1): a single uncaught throw anywhere (scene boot, an async chain) otherwise
// kills the rAF loop and leaves a dead canvas with no recovery and no signal. We catch it, report it
// (PII-free), and swap #game for a clean Reload panel. Best-effort + tiny — must NOT disturb a normal
// boot, so it only acts on an actual error event.
window.addEventListener('error', (ev) => {
  handleFatal('window.error', ev.error ?? ev.message, parent);
});
window.addEventListener('unhandledrejection', (ev) => {
  // A rejected promise is rarely fatal to the running game (the rAF loop survives), so we report it
  // but do NOT tear the canvas down — only true throws / explicit fatals show the recovery panel.
  reportFatal('unhandledrejection', ev.reason);
});

const boot = parseBootParams(window.location.search);

// FACE&FURY S4 STAGE: choose ONE Scale.FIT design space by the viewport aspect at boot — a portrait
// phone stage whose logical height matches tall phones, or a real 1280×800 landscape/desktop stage
// (board centred with gutter rails, menus composed for width). Pinned before the Game is built so every
// scene reads the chosen dims via isLandscape() + scene.scale.width/height.
const DESIGN = chooseDesignSpace();
setDesignSpace(DESIGN);

// TREATED-STAGE PALETTE SYNC (S4): the page backdrop (index.html) paints the letterbox bands. Its
// PALETTE-derived colours are declared there as CSS custom properties (with the current values as
// fallbacks); re-assert them here from PALETTE at boot so the CSS can never silently drift from the
// engine tokens. Bespoke gradient band stops stay literal in the CSS (they are not PALETTE tokens).
// Zero visual change while the values match — this is a drift GUARD, not a restyle.
try {
  const root = document.documentElement.style;
  const rgb = (n: number): string => `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
  root.setProperty('--tm-bd-void', hex(PALETTE.void)); // background-color + gradient anchor
  root.setProperty('--tm-bd-mid', hex(PALETTE.bg)); // the 34% mid stop
  root.setProperty('--tm-bd-grid-rgb', rgb(PALETTE.surfaceBorder)); // faint 80px grid lines
  root.setProperty('--tm-bd-bloom-rgb', rgb(PALETTE.player)); // cyan cabinet bloom
  root.setProperty('--tm-bd-glow-rgb', rgb(PALETTE.armed)); // warm amber counter-glow
} catch {
  /* headless / no document — the index.html :root fallbacks stand in */
}

// Orientation change across the portrait↔landscape boundary swaps the whole design space, so we relayout
// by RELOADING (documented S4 tradeoff — a live re-space would need every scene to rebuild mid-run, which
// is not worth the risk for the rare in-session rotation). Debounced; only fires when the aspect actually
// crosses the boundary the current stage was chosen on, so resizing WITHIN an orientation never reloads
// (and the fixed-viewport capture harness never triggers it).
//
// DAILY/LADDER SAFETY (S4 review item 9): a mid-board reload CANNOT waste a daily or ladder attempt. All
// attempt-consuming writes happen at BOARD END only — the daily streak in recordBoardOutcome (stats.ts,
// called from ResultScene), the ladder rung/gate in markRungBeaten/markGateBeaten (ResultScene), and the
// self-race ghost mint in recordDailyGhost (GameScene, gated on snap.status !== 'playing'). Nothing is
// persisted while a board is in play, and the reload re-launches the same URL (same ?daily=/seed →
// deterministic same board), so the only thing lost is uncommitted in-board progress. No suppression
// needed; if that invariant changes, gate this reload on snap.status !== 'playing' and reload at board end.
if (typeof window !== 'undefined') {
  let orientTimer: number | undefined;
  const onViewportChange = (): void => {
    if (orientTimer !== undefined) window.clearTimeout(orientTimer);
    orientTimer = window.setTimeout(() => {
      if (chooseDesignSpace().landscape !== DESIGN.landscape) {
        try {
          window.location.reload();
        } catch {
          /* headless / blocked reload — the stage simply stays as booted */
        }
      }
    }, 350);
  };
  window.addEventListener('resize', onViewportChange);
  window.addEventListener('orientationchange', onViewportChange);
}

const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  parent: 'game',
  backgroundColor: PALETTE.bg,
  scale: {
    mode: Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH,
    width: DESIGN.width,
    height: DESIGN.height,
  },
  // preserveDrawingBuffer is required for headless toDataURL()/screenshot() to read a drawn frame.
  render: { preserveDrawingBuffer: true, antialias: true, powerPreference: 'high-performance' },
  fps: { target: 60, min: 30 },
  scene: [BootScene, SplashScene, MenuScene, GameScene, ResultScene],
};

// Load the brand display face first (short timeout fallback so we never hang, incl. headless), THEN
// build the game so the first canvas text is rasterized in-font (no FOUT flash on the title/scores).
void loadFonts().finally(startGame);

function startGame(): void {
  let game: Phaser.Game;
  // Constructing the Game (WebGL context, scene boot) is the riskiest line at startup; if it throws
  // we want the recovery panel, not a half-built canvas and a dead loop.
  try {
    game = new Phaser.Game(config);
  } catch (err) {
    handleFatal('phaser.construct', err, parent);
    return;
  }

  try {
    // Stash boot params on the registry so every scene can read them.
    game.registry.set(REG.seed, boot.seed);
    game.registry.set(REG.topic, boot.topic);
    game.registry.set(REG.ai, boot.ai);
    game.registry.set(REG.autoPlay, boot.autoPlay);
    game.registry.set(REG.debug, boot.debug);
    game.registry.set(REG.daily, boot.daily);
    game.registry.set(REG.dailyMutator, boot.dailyMutator);
    game.registry.set(REG.challengeTarget, boot.challengeTarget);
    game.registry.set(REG.challengeGhost, boot.challengeGhost);
    game.registry.set(REG.ghostSource, boot.ghostSource);
    game.registry.set(REG.ghostRemoteId, boot.ghostRemoteId);
    game.registry.set(REG.series, boot.seriesMode);
    game.registry.set(REG.seriesGame, boot.seriesGame);
    game.registry.set(REG.seriesYou, boot.seriesYou);
    game.registry.set(REG.seriesThem, boot.seriesThem);
    game.registry.set(REG.boardIndex, 0);

    installHooks(game);
    installAnalyticsDebugBridge();
    trackFirstOpen();
    initExperiments();
    // Client side of the growth-config kill-switch: merge the served flags over the frozen-OFF default
    // (fail-closed), THEN init whatever growth SDK is allowed (native-only; a no-op on web + while dark).
    // Fire-and-forget — every growth surface stays dark until both the server flag and the interlocks agree.
    void loadGrowthConfig().then(() => initGrowth());

    // PWA (G17): register the offline app-shell service worker. Fire-and-forget; self-skips in
    // unsupported/insecure contexts so the eyes harness and any plain-http preview are unaffected.
    if (document.readyState === 'complete') void registerServiceWorker({ debug: boot.debug });
    else window.addEventListener('load', () => void registerServiceWorker({ debug: boot.debug }));

    if (boot.debug) {
      console.info('[trivia-munchers] boot', boot);
      // DEBUG-ONLY: expose the Game (scene-readiness probing) + window.__ui.open(panelId) so the
      // visual-parity capture harness can await scenes and force any modal/panel surface open
      // deterministically (no click-walking). Dynamic import keeps the bridge + its panel imports
      // out of the production bundle's eager path.
      (window as unknown as { __phaserGame?: Phaser.Game }).__phaserGame = game;
      void import('./render/debug/devBridge').then((m) => m.installDevBridge(game));
    }
  } catch (err) {
    handleFatal('startGame', err, parent);
  }
}
